import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useSelectedBusiness } from "../../../platform/redux/store";
import { GET_BUSINESS_CATEGORIES } from "../../../platform/graphql/queries";
import {
  BusinessCategoryLike,
  formatBusinessCategoryPath,
} from "../../../core/business/category";
import { UserContext } from "../../../platform/components/auth/auth-context";
import { hasStandaloneBookingAccess } from "../../../core/business/productAccess";

export default function CareSetupRequiredPage(): JSX.Element {
  const navigate = useNavigate();
  const selectedBusiness = useSelectedBusiness();
  const { providerUser } = React.useContext(UserContext);
  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);

  const selectedCategory = React.useMemo(() => {
    return (categoriesData?.getBusinessCategories || []).find(
      (category: BusinessCategoryLike) =>
        Number(category?.id) ===
        Number(selectedBusiness?.businessBasic?.businessCategoryId)
    );
  }, [categoriesData?.getBusinessCategories, selectedBusiness?.businessBasic?.businessCategoryId]);

  const categoryLabel =
    formatBusinessCategoryPath(selectedCategory) || "No category selected";
  const canSwitchToBooking = hasStandaloneBookingAccess(providerUser);

  return (
    <Box sx={{ px: 2, py: { xs: 3, sm: 5 } }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 760,
          mx: "auto",
          p: { xs: 2.5, sm: 4 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack spacing={2.25}>
          <Stack spacing={0.75}>
            <Typography variant="overline" color="text.secondary">
              Care Setup Required
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              Care businesses need a healthcare category
            </Typography>
          </Stack>

          <Alert severity="info">
            This workspace has the Care product added, but the business category
            does not look like a healthcare category yet. Update the business
            category before using Care residents and provider workflows.
          </Alert>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Current category:
            </Typography>
            <Chip label={categoryLabel} size="small" />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Choose a healthcare category for this business so it can use the Care workspace.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              onClick={() => navigate("/biz/business?v=care")}
            >
              Update Business Category
            </Button>
            {canSwitchToBooking && (
              <Button
                variant="outlined"
                onClick={() => navigate("/biz/bookingtypes?v=booking")}
              >
                Switch to Booking
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
